/**
 * React hook for accessing PostHog functionality.
 */

import { useEffect, useState } from 'react';
import { PostHogManager } from '../manager.js';
import type { FeatureFlagOptions } from '../types.js';

/**
 * Return type for usePostHog hook.
 */
export type UsePostHogReturn = {
  /** Whether PostHog is ready to use */
  ready: boolean;
  /** Get a feature flag value */
  getFeatureFlag: (flag: string, options?: FeatureFlagOptions) => Promise<boolean>;
  /** Identify a user */
  identify: (userId: string, properties?: Record<string, unknown>) => void;
  /** Capture an analytics event */
  capture: (event: string, properties?: Record<string, unknown>) => void;
  /** Set a group property */
  group: (type: string, key: string, properties?: Record<string, unknown>) => void;
  /** Reset PostHog state */
  reset: () => void;
  /** Manually refresh all feature flags */
  refreshFlags: () => Promise<void>;
  /** Trigger an immediate refresh of feature flags */
  triggerImmediateRefresh: () => Promise<void>;
  /** Subscribe to a specific feature flag changes */
  subscribeToFlag: (flag: string, callback: (value: boolean) => void) => () => void;
  /** Check if real-time updates are enabled */
  isRealTimeEnabled: () => boolean;
  /** Enable or disable real-time updates */
  setRealTimeEnabled: (enabled: boolean) => void;
};

/**
 * Hook to access PostHog functionality.
 *
 * @returns Object with PostHog methods and ready state
 *
 * @example
 * ```typescript
 * const { ready, getFeatureFlag, identify, capture } = usePostHog();
 *
 * if (ready) {
 *   capture('button_clicked', { buttonId: 'submit' });
 * }
 * ```
 */
export function usePostHog(): UsePostHogReturn {
  const manager = PostHogManager.getInstance();
  // Lazy init avoids a post-mount readiness flip when the manager is already
  // ready: if ready starts true, setReady(true) from the promise continuation
  // is a no-op (React bails out on same value). Without this, setReady always
  // causes a re-render on the first ready transition, minting new function
  // identities and re-firing any unguarded effects.
  const [ready, setReady] = useState(() => manager.isReady());

  useEffect(() => {
    let mounted = true;

    manager.waitForReady().then((isReady) => {
      if (mounted) {
        setReady(isReady);
      }
    });

    return () => {
      mounted = false;
    };
  }, [manager]);

  return {
    /**
     * Whether PostHog is ready to use.
     */
    ready,

    /**
     * Get a feature flag value.
     *
     * @param flag - Feature flag name
     * @param options - Options for flag retrieval
     * @returns Promise that resolves to the flag value
     */
    getFeatureFlag: (flag: string, options?: FeatureFlagOptions) =>
      manager.getFeatureFlag(flag, options),

    /**
     * Identify a user.
     *
     * @param userId - User identifier
     * @param properties - Optional user properties
     */
    identify: (userId: string, properties?: Record<string, unknown>) =>
      manager.identify(userId, properties),

    /**
     * Capture an analytics event.
     *
     * @param event - Event name
     * @param properties - Event properties
     */
    capture: (event: string, properties?: Record<string, unknown>) =>
      manager.capture(event, properties),

    /**
     * Set a group property.
     *
     * @param type - Group type
     * @param key - Group key
     * @param properties - Group properties
     */
    group: (type: string, key: string, properties?: Record<string, unknown>) =>
      manager.group(type, key, properties),

    /**
     * Reset PostHog state.
     */
    reset: () => manager.reset(),

    /**
     * Manually refresh all feature flags.
     *
     * @returns Promise that resolves when refresh is complete
     */
    refreshFlags: () => manager.refreshFlags(),

    /**
     * Trigger an immediate refresh of feature flags.
     *
     * @returns Promise that resolves when refresh is complete
     */
    triggerImmediateRefresh: () => manager.triggerImmediateRefresh(),

    /**
     * Subscribe to changes for a specific feature flag.
     *
     * @param flag - Feature flag name
     * @param callback - Callback function to call when flag changes
     * @returns Unsubscribe function
     */
    subscribeToFlag: (flag: string, callback: (value: boolean) => void) =>
      manager.subscribeToFlag(flag, callback),

    /**
     * Check if real-time updates are enabled.
     *
     * @returns True if real-time updates are enabled
     */
    isRealTimeEnabled: () => manager.isRealTimeEnabled(),

    /**
     * Enable or disable real-time updates.
     *
     * @param enabled - Whether to enable real-time updates
     */
    setRealTimeEnabled: (enabled: boolean) => manager.setRealTimeEnabled(enabled)
  };
}
